import { useState, useEffect } from 'react';
import { Input } from '../components/ui/input';
import { Search, Filter } from 'lucide-react';
import { getDreams } from '../lib/api';
import DreamCard from '../components/DreamCard';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Button } from '../components/ui/button';

const ONIRVA_ICON = "https://customer-assets.emergentagent.com/job_200ec0e0-0820-4417-b1b5-b6286eb18bc4/artifacts/c3nyk9fu_80AC8250-CA42-4BF9-BA0D-F1211AA6A764.png";

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'analyzed', label: 'Analyzed Only' },
  { value: 'unanalyzed', label: 'Not Analyzed' },
];

export default function Journal() {
  const [dreams, setDreams] = useState([]);
  const [filteredDreams, setFilteredDreams] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    const fetchDreams = async () => {
      try {
        const response = await getDreams(100);
        setDreams(response.data);
        setFilteredDreams(response.data);
      } catch (error) {
        console.error('Failed to fetch dreams:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchDreams();
  }, []);
  
  useEffect(() => {
    let result = [...dreams];
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(dream => 
        dream.title.toLowerCase().includes(query) ||
        dream.content.toLowerCase().includes(query) ||
        dream.symbols?.some(s => s.toLowerCase().includes(query))
      );
    }
    
    // Apply sort/filter
    switch (sortBy) {
      case 'oldest':
        result.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        break;
      case 'analyzed':
        result = result.filter(d => d.analysis);
        break;
      case 'unanalyzed':
        result = result.filter(d => !d.analysis);
        break;
      default: // newest
        result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }
    
    setFilteredDreams(result);
  }, [dreams, searchQuery, sortBy]);
  
  return (
    <div className="min-h-screen bg-background pt-20 pb-8" data-testid="journal-page">
      <div className="hero-gradient absolute inset-0" />
      
      <div className="relative z-10">
        {/* Header */}
        <header className="p-6 pt-8">
          <h1 className="text-2xl font-serif font-bold mb-1">Dream Journal</h1>
          <p className="text-muted-foreground text-sm">{dreams.length} dreams recorded</p>
        </header>
        
        {/* Search and Filter */}
        <div className="px-6 mb-6 flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search dreams..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-muted/50 border-border rounded-xl"
              data-testid="search-input"
            />
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="rounded-xl" data-testid="filter-btn">
                <Filter className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {SORT_OPTIONS.map((option) => (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => setSortBy(option.value)}
                  className={sortBy === option.value ? 'bg-primary/10 text-primary' : ''}
                >
                  {option.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        {/* Dreams List */}
        <div className="px-6">
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="glass-card p-4 h-28 skeleton" />
              ))}
            </div>
          ) : filteredDreams.length === 0 ? (
            <div className="glass-card p-8 text-center">
              <img src={ONIRVA_ICON} alt="" className="w-16 h-16 mx-auto mb-4 opacity-50 rounded-xl" />
              {searchQuery ? (
                <>
                  <h3 className="font-serif font-bold mb-2">No matches found</h3>
                  <p className="text-sm text-muted-foreground">
                    Try a different search term
                  </p>
                </>
              ) : (
                <>
                  <h3 className="font-serif font-bold mb-2">No dreams recorded yet</h3>
                  <p className="text-sm text-muted-foreground">
                    Your dreams will appear here over time.
                  </p>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredDreams.map((dream) => (
                <DreamCard 
                  key={dream.dream_id} 
                  dream={dream}
                  onDelete={(deletedId) => {
                    setDreams(prev => prev.filter(d => d.dream_id !== deletedId));
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
